import { venueZone } from "../format.js";
import { useCallback, useEffect, useState } from "react";
import { useApp } from "../context.js";
import { tabletsApi, type ShownTablet } from "../tablets-api.js";
import type { OrgMember, OrgVenue } from "./api.js";

function when(iso: string | null): string {
  if (!iso) return "never";
  return new Date(iso).toLocaleString("en-AU", { timeZone: venueZone(), day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
}

/**
 * Screen 12 › Tablets (#80): the organisation's enrolled tablets and their
 * revocation — one click to cut off a lost or stolen one — and clearing a
 * person's PIN when they have forgotten it or locked it with wrong guesses.
 *
 * A tablet is enrolled from the tablet itself (the account panel, signed in
 * as a manager), not from here: the key it is given must end up on that
 * device and nowhere else.
 */
export function Tablets(props: { readonly venues: readonly OrgVenue[]; readonly members: readonly OrgMember[] }) {
  const app = useApp();
  const [tablets, setTablets] = useState<readonly ShownTablet[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setTablets((await tabletsApi.list(app.token)).devices);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not read the tablets");
    }
  }, [app.token]);

  useEffect(() => {
    void load();
  }, [load]);

  const venueName = (id: string) => props.venues.find((v) => v.venueId === id)?.name ?? id;

  return (
    <>
      <div className="org-section">
        <h3>Tablets{tablets ? ` · ${tablets.filter((t) => !t.revokedAt).length}` : ""}</h3>
        <div className="org-note">
          A tablet opens to a PIN screen for one venue, so a shared device never means a shared password. To add
          one, sign in on it as a manager and choose "Set up as this venue's tablet" in the account panel. Revoking
          stops it on its very next request.
        </div>
        {error && <div className="msg err">{error}</div>}
        {msg && <div className="msg ok">{msg}</div>}
        {tablets === null && !error && <div className="label">Reading the tablets…</div>}
        {tablets?.length === 0 && <div className="org-note">No tablets enrolled yet.</div>}
        {tablets && tablets.length > 0 && (
          <div className="table">
            {tablets.map((t) => (
              <div className="trow org-trow tablet-row" key={t.deviceId}>
                <span className="m-name">
                  <span className="n">{t.name}</span>
                  <span className="s">
                    {venueName(t.venueId)} · enrolled {when(t.enrolledAt)} · last used {when(t.lastSeenAt)}
                  </span>
                </span>
                <span className="spacer" />
                {t.revokedAt ? (
                  <span className="label">Revoked {when(t.revokedAt)}</span>
                ) : (
                  <button
                    className="btn sm danger"
                    disabled={busy !== null}
                    onClick={async () => {
                      if (!window.confirm(`Revoke "${t.name}"? It stops working at once and would have to be set up again.`)) return;
                      setBusy(t.deviceId);
                      setError(null);
                      try {
                        await tabletsApi.revoke(app.token, t.deviceId);
                        setMsg(`${t.name} is revoked.`);
                        await load();
                      } catch (err) {
                        setError(err instanceof Error ? err.message : "Could not revoke it");
                      } finally {
                        setBusy(null);
                      }
                    }}
                  >
                    Revoke
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="org-section">
        <h3>Clear a PIN</h3>
        <div className="org-note">
          For someone who has forgotten their PIN or locked it with wrong guesses. It is removed, not reset: they set
          a new one themselves from their own sign-in. Nobody ever sees anyone else's PIN.
        </div>
        <div className="table">
          {props.members.map((m) => (
            <div className="trow org-trow tablet-row" key={m.userId}>
              <span className="m-name">
                <span className="n">{m.displayName || m.email}</span>
                <span className="s">{m.email}</span>
              </span>
              <span className="spacer" />
              <button
                className="btn sm"
                disabled={busy !== null}
                onClick={async () => {
                  setBusy(m.userId);
                  setError(null);
                  try {
                    await tabletsApi.clearPin(app.token, m.userId);
                    setMsg(`${m.displayName || m.email}'s PIN is cleared; they can set a new one.`);
                  } catch (err) {
                    setError(err instanceof Error ? err.message : "Could not clear it");
                  } finally {
                    setBusy(null);
                  }
                }}
              >
                Clear PIN
              </button>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
